import type { SupabaseClient } from "@supabase/supabase-js";
import type { Database } from "./persistence/database.generated";
import type { AyzeoHistoricalPromptInsights, AyzeoInsightsModel, AyzeoInsightsTimeRange } from "./ayzeo-prompt-insights";

export type HostedAyzeoMcpRetrievalResult = { batchId?: string; status: "committed" | "idempotent_replay"; message: string };
export type HostedAyzeoMcpConnectionTestResult = { status: "smoke"; message: string };
export type HostedAyzeoPromptInsightsResult = { status:"historical_insights"; insights:AyzeoHistoricalPromptInsights };
export type HostedAyzeoPromptInsightsRequest = { organisationId:string; auditEngagementId:string; promptSnapshotId:string; model:AyzeoInsightsModel; timeRange?:AyzeoInsightsTimeRange; startDate?:string; endDate?:string };
export interface HostedAyzeoMcpRetrievalPort {
  retrieve(input: { organisationId: string; auditEngagementId: string }): Promise<HostedAyzeoMcpRetrievalResult>;
  testConnection(input: { organisationId: string; auditEngagementId: string }): Promise<HostedAyzeoMcpConnectionTestResult>;
  promptInsights(input:HostedAyzeoPromptInsightsRequest):Promise<HostedAyzeoPromptInsightsResult>;
}

/** Browser boundary: it sends only organisation and audit identifiers to the authenticated function. */
export function createHostedAyzeoMcpRetrievalPort(client: SupabaseClient<Database>): HostedAyzeoMcpRetrievalPort {
  const invoke=async(input:{ organisationId:string; auditEngagementId:string }|HostedAyzeoPromptInsightsRequest,operation:"retrieve"|"smoke"|"prompt_insights")=>{
    const { data, error } = await client.functions.invoke("ayzeo-mcp-readonly-retrieval", { body: { schemaVersion: 1, operation, ...input } });
    if (error) {
      const context = error.context as Response | undefined;
      const body = context ? await context.clone().json().catch(() => undefined) as { message?: unknown } | undefined : undefined;
      throw new Error(typeof body?.message === "string" ? body.message : operation==="smoke"?"The Ayzeo connection test could not be completed.":"Ayzeo retrieval could not be completed.");
    }
    return data;
  };
  return {
    async retrieve(input) {
      const data=await invoke(input,"retrieve");
      if (!data || typeof data !== "object" || !["committed", "idempotent_replay"].includes(String((data as { status?: unknown }).status))) throw new Error("Ayzeo retrieval returned an invalid server response.");
      return data as HostedAyzeoMcpRetrievalResult;
    },
    async testConnection(input) {
      const data=await invoke(input,"smoke");
      if (!data || typeof data !== "object" || String((data as { status?: unknown }).status)!=="smoke") throw new Error("Ayzeo connection testing returned an invalid server response.");
      return data as HostedAyzeoMcpConnectionTestResult;
    },
    async promptInsights(input) {
      const data=await invoke(input,"prompt_insights");
      if (!data || typeof data !== "object" || String((data as { status?: unknown }).status)!=="historical_insights" || !(data as {insights?:unknown}).insights) throw new Error("Ayzeo historical insights returned an invalid server response.");
      return data as HostedAyzeoPromptInsightsResult;
    }
  };
}
